import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, Clock, Factory, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function MobileWarehouseWOListPage() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchWorkOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/work-orders`);
      // Show active WOs: in_production, staged, scheduled
      const active = (res.data || []).filter(wo =>
        ['in_production', 'staged', 'scheduled'].includes(wo.status)
      );
      setWorkOrders(active);
    } catch (err) {
      console.error('Failed to fetch WOs:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkOrders();
    const interval = setInterval(fetchWorkOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchWorkOrders]);

  const statusLabel = { scheduled: 'Scheduled', staged: 'Staged', in_production: 'Running' };
  const statusOrder = { in_production: 0, staged: 1, scheduled: 2 };

  const sorted = [...workOrders].sort((a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9));

  if (loading) {
    return (
      <div className="mobile-page">
        <div className="mobile-loading"><div className="mobile-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="mobile-page">
      <button className="mobile-back-btn" onClick={() => navigate('/mobile/warehouse')}>
        <ArrowLeft size={18} /> Warehouse
      </button>

      <div className="mobile-page-header">
        <div style={{ flex: 1 }}>
          <div className="mobile-page-title">Work Orders</div>
          <div className="mobile-page-subtitle">{workOrders.length} active</div>
        </div>
        <button
          onClick={() => { setRefreshing(true); fetchWorkOrders(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#007aff', background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {sorted.length === 0 ? (
        <div className="mobile-empty-state">
          <Factory />
          <p>No active work orders</p>
        </div>
      ) : (
        sorted.map(wo => {
          const progress = wo.planned_quantity > 0 ? ((wo.actual_quantity || 0) / wo.planned_quantity) * 100 : 0;
          const showProgress = wo.status === 'in_production';
          return (
            <div
              key={wo.wo_id}
              className={`mobile-wo-card status-${wo.status}`}
              onClick={() => navigate(`/mobile/warehouse/wo/${wo.wo_id}`)}
            >
              <div className="mobile-wo-header">
                <div>
                  <div className="mobile-wo-number">{wo.wo_number} · Line {wo.production_line}</div>
                  <div className="mobile-wo-item-name">{wo.item_name}</div>
                </div>
                <span className={`mobile-status-badge ${wo.status}`}>
                  {statusLabel[wo.status] || wo.status}
                </span>
              </div>
              <div className="mobile-wo-meta">
                <span className="mobile-wo-meta-item">
                  <Clock size={14} />
                  {wo.planned_start_time || 'TBD'} - {wo.planned_end_time || 'TBD'}
                </span>
                <span className="mobile-wo-meta-item">
                  <Package size={14} />
                  {showProgress ? `${wo.actual_quantity || 0} / ` : ''}{wo.planned_quantity} cases
                </span>
              </div>
              {showProgress && (
                <div className="mobile-wo-progress">
                  <div className="mobile-wo-progress-bar">
                    <div
                      className={`mobile-wo-progress-fill ${progress >= 100 ? 'green' : progress > 50 ? 'blue' : 'orange'}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                  <div className="mobile-wo-progress-text">
                    <span>{progress.toFixed(0)}%</span>
                    <span>{wo.actual_quantity || 0} / {wo.planned_quantity}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
